import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import {
  sendMessage,
  checkNumberExists,
  getContacts,
  getMessageLimits,
  uploadMessageAttachment,
  getActiveSendJob,
  getSendJobProgress,
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { Send, Loader2, CheckCircle, XCircle, Search, Phone, Users, AlertTriangle, Lightbulb } from 'lucide-react';
import EmojiPicker from '../components/EmojiPicker';
import FileAttachment from '../components/FileAttachment';
import { cn } from '../lib/utils';

// Button that morphs into a progress bar while a send_job is in flight.
// Same dimensions as a regular Button (h-10) — no layout shift.
function SendButtonOrProgress({ job, onClick, disabled, label }) {
  if (!job) {
    return (
      <Button
        type="submit"
        className="w-full"
        disabled={disabled}
        onClick={onClick}
        data-testid="send-submit-button"
      >
        <Send className="w-4 h-4 mr-2" />
        {label}
      </Button>
    );
  }
  const total = job.total || 0;
  const processed = job.processed || 0;
  const percent = total > 0 ? Math.round((processed / total) * 100) : 0;
  const isDone = job.status === 'completed';
  const hasFailures = (job.failed || 0) > 0;
  const barColor = isDone
    ? hasFailures
      ? 'bg-orange-500'
      : 'bg-green-500'
    : 'bg-primary';
  const containerColor = isDone
    ? hasFailures
      ? 'bg-orange-50 border-orange-300 text-orange-900 dark:bg-orange-900/20 dark:text-orange-200'
      : 'bg-green-50 border-green-300 text-green-900 dark:bg-green-900/20 dark:text-green-200'
    : 'bg-secondary border-input text-foreground';
  return (
    <div
      role="progressbar"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-live="polite"
      className={cn(
        'relative w-full h-10 rounded-md overflow-hidden border',
        'flex items-center justify-center text-sm font-medium select-none',
        'transition-colors duration-300',
        containerColor
      )}
      data-testid="send-progress-bar"
    >
      <div
        className={cn('absolute inset-y-0 left-0 transition-all duration-500 ease-out opacity-80', barColor)}
        style={{ width: `${isDone ? 100 : percent}%` }}
      />
      <span className="relative z-10 flex items-center gap-2">
        {isDone ? (
          <>
            <CheckCircle className="w-4 h-4" />
            {hasFailures
              ? `${job.sent} enviada(s), ${job.failed} falha(s)`
              : `${job.sent} enviada(s)`}
          </>
        ) : (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Enviando {processed}/{total}
          </>
        )}
      </span>
    </div>
  );
}

export default function SendMessagePage() {
  const [sendMode, setSendMode] = useState('single'); // 'single' or 'contacts'
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [searchContact, setSearchContact] = useState('');
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [numberStatus, setNumberStatus] = useState(null);
  const [isUnlimited, setIsUnlimited] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  // activeJob: { job_id, status, total, processed, sent, failed } or null
  const [activeJob, setActiveJob] = useState(null);
  // Confirmation dialog for bulk sends (2+ contacts) — see handleSend / executeSend below
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    fetchContacts();
    fetchLimits();
    // On mount, check if there's a running send job for this tenant.
    // If yes, the UI immediately shows the progress bar instead of the Send button.
    checkActiveJob();
  }, []);

  // Polling of the active job + auto-stop when complete (with a 3s "done" flash).
  useEffect(() => {
    if (!activeJob || !activeJob.job_id) return;
    if (activeJob.status === 'completed') {
      // Already finished — show the green/orange bar for 3s, then clear.
      const t = setTimeout(() => setActiveJob(null), 3000);
      return () => clearTimeout(t);
    }
    let intervalId;
    const tick = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const res = await getSendJobProgress(activeJob.job_id);
        setActiveJob((prev) => (prev ? { ...prev, ...res.data } : prev));
      } catch {
        // network blip — next tick will retry
      }
    };
    intervalId = setInterval(tick, 2000);
    const onVis = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVis);
    // Fire one immediate fetch so the bar updates without waiting 2s
    tick();
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [activeJob?.job_id, activeJob?.status]);

  const checkActiveJob = async () => {
    try {
      const res = await getActiveSendJob();
      if (res.data && res.data.active) {
        setActiveJob({ ...res.data.active });
      }
    } catch {
      // silent
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await getContacts({ size: 500 });
      setContacts(response.data.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const fetchLimits = async () => {
    try {
      const response = await getMessageLimits();
      setIsUnlimited(response.data.unlimited === true);
    } catch (error) {
      console.error('Error fetching limits:', error);
    }
  };

  const handleCheckNumber = async () => {
    if (!phone) return;
    setChecking(true);
    setNumberStatus(null);

    try {
      const response = await checkNumberExists(phone);
      setNumberStatus(response.data);
    } catch (error) {
      toast.error('Erro ao verificar número');
    } finally {
      setChecking(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();

    // Block submission while a job is in flight (also prevents Enter-key bypass)
    if (activeJob && activeJob.status !== 'completed') {
      return;
    }

    if (sendMode === 'single' && !phone) {
      toast.error('Informe o número de telefone');
      return;
    }

    if (sendMode === 'contacts' && selectedContacts.length === 0) {
      toast.error('Selecione pelo menos um contato');
      return;
    }

    if (!message.trim()) {
      toast.error('Digite uma mensagem');
      return;
    }

    // Bulk-send confirmation: 2+ contacts → user must explicitly acknowledge
    // that sends cannot be cancelled once started (WhatsApp limitation).
    if (sendMode === 'contacts' && selectedContacts.length >= 2) {
      setConfirmDialogOpen(true);
      return;
    }

    // 1 recipient → send directly without prompting
    await executeSend();
  };

  const executeSend = async () => {
    setConfirmDialogOpen(false);
    setSending(true);

    try {
      const payload = {
        message: message.trim(),
      };

      if (sendMode === 'single') {
        payload.phone = phone;
      } else {
        payload.contact_ids = selectedContacts;
      }

      // Upload attachment first if selected
      if (selectedFile && selectedFile.file) {
        try {
          const attachmentResponse = await uploadMessageAttachment(selectedFile.file);
          payload.attachment_id = attachmentResponse.data.attachment_id;
        } catch (attachmentError) {
          toast.error(attachmentError.response?.data?.detail || 'Erro ao enviar anexo');
          setSending(false);
          return;
        }
      }

      const response = await sendMessage(payload);

      // New flow: backend returns { job_id, total, status: "running" }.
      // The actual sending happens in background — UI flips to progress bar
      // and the useEffect above polls for updates.
      if (response.data && response.data.job_id) {
        setActiveJob({
          job_id: response.data.job_id,
          status: response.data.status || 'running',
          total: response.data.total || 0,
          processed: 0,
          sent: 0,
          failed: 0,
        });
        toast.success('Envio iniciado');
      }

      // Clear form — the job is independent of this view's state now
      setPhone('');
      setMessage('');
      setSelectedContacts([]);
      setNumberStatus(null);
      setSelectedFile(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao iniciar envio');
    } finally {
      setSending(false);
    }
  };

  const toggleContact = (contactId) => {
    setSelectedContacts((prev) =>
      prev.includes(contactId)
        ? prev.filter((id) => id !== contactId)
        : [...prev, contactId]
    );
  };

  const selectAllContacts = () => {
    if (selectedContacts.length === filteredContacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(filteredContacts.map(c => c._id));
    }
  };

  const handleEmojiSelect = (emoji) => {
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newMessage = message.substring(0, start) + emoji + message.substring(end);
      setMessage(newMessage);
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + emoji.length, start + emoji.length);
      }, 0);
    } else {
      setMessage(message + emoji);
    }
  };

  const filteredContacts = contacts.filter(
    (c) =>
      c.name.toLowerCase().includes(searchContact.toLowerCase()) ||
      c.phone.includes(searchContact)
  );

  const canSend = sendMode === 'single' 
    ? phone && message.trim() 
    : selectedContacts.length > 0 && message.trim();

  return (
    <div className="flex gap-6 max-w-4xl mx-auto" style={{ contain: 'paint' }}>
      {/* Main Card */}
      <Card className="flex-1 max-w-2xl mx-auto">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
              <Send className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            </div>
            <div>
              <CardTitle>Enviar Mensagem</CardTitle>
              <CardDescription>
                Envie mensagens para um número único ou para uma lista de contatos{' '}
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-primary hover:underline font-medium align-baseline"
                      data-testid="tips-trigger"
                    >
                      <Lightbulb className="w-3.5 h-3.5" />
                      Dicas
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72" align="start">
                    <p className="text-base font-semibold text-gray-700 dark:text-gray-300 mb-2">💡 Dicas</p>
                    <ul className="space-y-2 text-xs text-muted-foreground">
                      <li>• Formato: +55 11 99999-9999</li>
                      <li>• Verifique o número antes de enviar</li>
                      <li>• Use a busca para filtrar contatos</li>
                      <li>• Mensagens são enviadas em fila</li>
                    </ul>
                  </PopoverContent>
                </Popover>
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {/* sem overflow-hidden: ele cortava o painel de emojis, que é posicionado
              de forma absoluta e precisa transbordar o card */}
          <form onSubmit={handleSend} className="space-y-4">
            {/* Send Mode Tabs */}
            <Tabs value={sendMode} onValueChange={setSendMode} className="w-full">
              <TabsList className="grid w-full grid-cols-2 h-9 p-0.5">
                <TabsTrigger 
                  value="single" 
                  className="flex items-center gap-2 py-1.5 text-sm data-[state=inactive]:bg-slate-500 data-[state=inactive]:text-white"
                >
                  <Phone className="w-3.5 h-3.5" />
                  Número Único
                </TabsTrigger>
                <TabsTrigger 
                  value="contacts" 
                  className="flex items-center gap-2 py-1.5 text-sm data-[state=inactive]:bg-slate-500 data-[state=inactive]:text-white"
                >
                  <Users className="w-3.5 h-3.5" />
                  Lista de Contatos
                </TabsTrigger>
              </TabsList>

              {/* Single Number Tab */}
              <TabsContent value="single" className="mt-4">
                <div className="h-[240px] space-y-2">
                  <Label htmlFor="phone">Número de Telefone</Label>
                  <div className="flex gap-2">
                    <Input
                      id="phone"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+5511999999999"
                      className="flex-1"
                      data-testid="send-to-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCheckNumber}
                      disabled={!phone || checking}
                      className="hover:border-primary hover:text-primary hover:bg-primary/5"
                      title="Verificar número no WhatsApp"
                    >
                      {checking ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Phone className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  {numberStatus && (
                    <div className="flex items-center gap-2 text-sm">
                      {numberStatus.exists ? (
                        <>
                          <CheckCircle className="w-4 h-4 text-teal-600" />
                          <span className="text-teal-600">Número válido no WhatsApp</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="w-4 h-4 text-red-600" />
                          <span className="text-red-600">Número não encontrado no WhatsApp</span>
                        </>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Use o formato internacional: +55 11 99999-9999
                  </p>
                </div>
              </TabsContent>

              {/* Contact List Tab */}
              <TabsContent value="contacts" className="mt-4">
                <div className="h-[240px] space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Selecione os Contatos</Label>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={selectAllContacts}
                    >
                      {selectedContacts.length === filteredContacts.length && filteredContacts.length > 0
                        ? 'Desmarcar Todos'
                        : 'Selecionar Todos'}
                    </Button>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar contatos..."
                      value={searchContact}
                      onChange={(e) => setSearchContact(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <div className="h-[160px] overflow-y-auto border rounded-lg p-2 space-y-1">
                    {filteredContacts.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        Nenhum contato encontrado
                      </p>
                    ) : (
                      filteredContacts.map((contact) => (
                        <label
                          key={contact._id}
                          className="flex items-center gap-3 p-1.5 rounded hover:bg-secondary cursor-pointer"
                        >
                          <Checkbox
                            checked={selectedContacts.includes(contact._id)}
                            onCheckedChange={() => toggleContact(contact._id)}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{contact.name}</p>
                            <p className="text-xs text-muted-foreground">{contact.phone}</p>
                          </div>
                        </label>
                      ))
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {selectedContacts.length} de {contacts.length} contato(s) selecionado(s)
                  </p>
                </div>
              </TabsContent>
            </Tabs>

            {/* Message */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="message">Mensagem</Label>
                {/* abre para cima: aqui o botão fica na parte de baixo da tela */}
                <EmojiPicker direction="up" onEmojiSelect={handleEmojiSelect} />
              </div>
              <Textarea
                id="message"
                ref={textareaRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Digite sua mensagem aqui..."
                rows={5}
                required
                data-testid="send-message-textarea"
              />
              <p className="text-xs text-muted-foreground text-right">
                {message.length} caracteres
              </p>
            </div>

            {/* File Attachment */}
            <div className="space-y-2">
              <Label>Anexo (opcional)</Label>
              <FileAttachment
                onFileSelect={setSelectedFile}
                selectedFile={selectedFile}
                onRemove={() => setSelectedFile(null)}
                disabled={sending}
                isUnlimited={isUnlimited}
              />
            </div>

            {/* Submit — Button morphs into a progress bar while a send_job is active */}
            <SendButtonOrProgress
              job={activeJob}
              disabled={sending || !canSend || !!activeJob}
              label={
                sending
                  ? 'Iniciando…'
                  : `Enviar Mensagem${
                      sendMode === 'contacts' && selectedContacts.length > 0
                        ? ` (${selectedContacts.length})`
                        : ''
                    }`
              }
            />
          </form>
        </CardContent>
      </Card>

      {/* Bulk send confirmation dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Confirmar envio em massa
            </DialogTitle>
            <DialogDescription className="pt-2 text-base">
              Você está prestes a enviar a mensagem para{' '}
              <strong className="text-foreground">{selectedContacts.length} contatos</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm text-muted-foreground space-y-2">
            <p>
              Devido à natureza do próprio WhatsApp,{' '}
              <strong className="text-foreground">não é possível cancelar o envio</strong>{' '}
              após iniciado: as mensagens serão entregues à fila do WhatsApp uma a uma.
            </p>
            <p>Deseja confirmar o envio?</p>
          </div>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmDialogOpen(false)}
              data-testid="send-confirm-cancel"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={executeSend}
              data-testid="send-confirm-ok"
            >
              <Send className="w-4 h-4 mr-2" />
              Confirmar envio
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
